import PropTypes from "prop-types";
import { Divider, Skeleton } from "antd";
import { formatInCompanyTimeZone, timeZoneAbbreviation } from "../../../../../helpers/operationalHours";
import { getExtensionDueAmount } from "../../../lib/reservationExtension/extensionApi";

const DATE_TIME_FORMAT = "MMM D, YYYY h:mm A";

const money = (value) => `$${(Number(value) || 0).toFixed(2)}`;

const Row = ({ label, value, className = "" }) => (
    <div className={`flex items-center justify-between gap-4 ${className}`}>
        <p className="fontRegular text-[#7f8c8d]">{label}</p>
        <p className="fontSemi text-right text-[#2F2F46]">{value}</p>
    </div>
);

Row.propTypes = {
    label: PropTypes.node.isRequired,
    value: PropTypes.node,
    className: PropTypes.string,
};

/**
 * Step 2 of the extend modal: the schedule change and the re-priced full period, ending in what
 * the extension costs on top of what has already been paid. Times are shown on the branch's clock.
 */
const ExtendInvoiceSummary = ({ invoice, isLoading, reservation, newDropoffDate, timeZone, t }) => {
    const currentDropoff = reservation?.exactDropoffTime || reservation?.dropoffDate;
    const zone = timeZoneAbbreviation(timeZone);
    const format = (instant) => {
        const formatted = formatInCompanyTimeZone(instant, timeZone, DATE_TIME_FORMAT);
        return formatted ? `${formatted} ${zone}`.trim() : "—";
    };

    const alreadyPaid = Number(reservation?.invoice?.deposit || 0);
    const extensionDue = getExtensionDueAmount(invoice, reservation);

    return (
        <div className="space-y-2 rounded-xl border border-[#EEF0F6] p-4 text-sm sm:p-6">
            <Row label={t("extendReservation.invoice.pickup")} value={format(reservation?.pickupDate)} />
            <Row label={t("extendReservation.invoice.currentDropoff")} value={format(currentDropoff)} />
            <Row label={t("extendReservation.invoice.newDropoff")} value={format(newDropoffDate)} />

            <Divider className="!my-3" />

            {isLoading ? (
                <Skeleton active paragraph={{ rows: 5 }} title={false} />
            ) : (
                <>
                    {invoice?.charges?.map((item, index) => (
                        <Row
                            key={index}
                            label={<span className="capitalize">{`${item?.description} x ${item?.unit}`}</span>}
                            value={money(item?.total)}
                        />
                    ))}

                    <Row
                        label={t("extendReservation.invoice.estimatedTotal")}
                        value={money(invoice?.estimatedTotal)}
                        className="pt-1"
                    />

                    {invoice?.taxes?.map((item, index) => (
                        <Row
                            key={index}
                            label={
                                <span className="capitalize">
                                    {item?.taxName}{" "}
                                    {item?.taxType === "%" ? `(${item?.taxValue}%)` : `($${item?.taxValue})`}
                                </span>
                            }
                            value={money(item?.total)}
                        />
                    ))}

                    <Divider className="!my-3" />

                    <Row label={t("extendReservation.invoice.total")} value={money(invoice?.total)} />
                    <Row label={t("extendReservation.invoice.alreadyPaid")} value={`-${money(alreadyPaid)}`} />

                    <div className="mt-3 flex items-center justify-between rounded-lg bg-[#5D5DF6]/[0.06] px-3 py-2">
                        <p className="fontSemi text-[#5D5DF6]">{t("extendReservation.invoice.extensionDue")}</p>
                        <p className="fontSemi text-[#5D5DF6]">{money(extensionDue)}</p>
                    </div>
                </>
            )}
        </div>
    );
};

ExtendInvoiceSummary.propTypes = {
    invoice: PropTypes.object,
    isLoading: PropTypes.bool,
    reservation: PropTypes.object,
    newDropoffDate: PropTypes.string,
    timeZone: PropTypes.string,
    t: PropTypes.func.isRequired,
};

export default ExtendInvoiceSummary;
